import React, { FC, memo, useMemo, useRef } from "react";
import "./AnnotationTableControls.scss";
import { Skeleton, Table } from "antd";
import type { ColumnType, TableRef } from "antd/es/table";
import { GroupBlockData, useHotKeyListener } from "../../configMap";
import { GroupBlockTableData } from "../interfaces/groupBlockTable";
import GroupBlockTabs from "./groupBlockTabs/GroupBlockTabs";
import { GROUPBLOCK_TABS_HEIGHT, TABLE_HEADER_HEIGHT } from "../constants";
import useAnnotationTableControls from "../hooks/useAnnotationTableControls";
import useVirtualTableScroll from "../hooks/useVirtualTableScroll";
import HeaderActions from "./headerActions/HeaderActions";
import Actions from "./actions/Actions";
import useHighlightEntities from "../../annotation/hooks/useHighlightEntities";
import TableEntityCell from "./tableEntityCell/TableEntityCell";
import CategorizationCellSwitch from "./tableCategorizationCell/categorizationCellSwitch/CategorizationCellSwitch";
import { useAppSelector } from "../../app";
import selectHighlightedIndex from "../../annotation/selectors/selectHighlightedIndex";
import LinkIcon from "../../metadata/components/categorization/LinkIcon";
import EntityTitle from "../../configMap/components/entity/EntityTitle";
import { shallowEqual } from "react-redux";

type Props = {
  disabled: boolean;
  multipleLinesGroupBlocks: Array<GroupBlockData>;
  hideNonRequiredEntities: boolean;
  handleResizing: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
};

const AnnotationTableControls: FC<Props> = ({
  disabled,
  multipleLinesGroupBlocks,
  hideNonRequiredEntities,
  handleResizing,
}) => {
  const tableRef = useRef<TableRef>(null);
  const { highlightEntitiesInGroupBlock } = useHighlightEntities();
  const highlightedIndex = useAppSelector(selectHighlightedIndex, shallowEqual);

  const {
    selectedGroupBlockForTable,
    selectedGroupBlock,
    addGroupBlockLineAction,
    configMapLoading,
    refHeightContainer,
    currentHeight,
  } = useAnnotationTableControls(disabled, multipleLinesGroupBlocks);

  useHotKeyListener(
    hideNonRequiredEntities,
    disabled,
    selectedGroupBlockForTable?.groupBlock.groupBlockEntityTypes,
    selectedGroupBlockForTable?.groupBlock.multipleGroupBlocks,
  );

  useVirtualTableScroll(tableRef, selectedGroupBlockForTable?.data);

  const actionColumnItem =
    useMemo((): ColumnType<GroupBlockTableData> | null => {
      if (!selectedGroupBlock) {
        return null;
      }

      return {
        title: (
          <HeaderActions
            groupBlock={selectedGroupBlock}
            index={selectedGroupBlock?.index}
            disabled={disabled}
          />
        ),
        key: "actions",
        width: 55,
        fixed: "right",
        render: (_: unknown, props: GroupBlockTableData) => {
          if (props.isPageBreak) {
            return {
              props: { colSpan: 0 },
            };
          }
          return (
            <Actions
              groupBlock={props.groupBlock}
              index={props.index}
              amountOfLines={props.count}
              disabled={disabled}
              addGroupBlock={() => {
                addGroupBlockLineAction(props.groupBlock, props.index);
              }}
            />
          );
        },
      };
    }, [selectedGroupBlock, addGroupBlockLineAction, disabled]);

  const columnItems = useMemo(() => {
    // `useGroupBlockForTable` returns a table only when a group block is
    // selected, and `actionColumnItem` is built from that same group block, so
    // the second check is never the one that trips — it just lets the column
    // list below be typed without a null member.
    if (!selectedGroupBlockForTable || !actionColumnItem) {
      return [];
    }

    const entityColumnItems = selectedGroupBlockForTable.entityColumnItems.map(
      (item) => {
        return {
          title: (
            <EntityTitle
              disabled={disabled}
              groupBlockEntityType={item.groupBlockEntityType}
              entityTypes={
                selectedGroupBlockForTable.groupBlock.groupBlockEntityTypes
              }
            />
          ),
          key: item.key,
          dataIndex: item.key,
          width: item.width,
          render: (_: unknown, record: GroupBlockTableData) => {
            if (record.isPageBreak) {
              return {
                props: { colSpan: 0 },
              };
            }
            return (
              <TableEntityCell
                groupBlockEntityType={item.groupBlockEntityType}
                groupBlockIndex={record.index}
                disabled={item.disabled}
              />
            );
          },
        };
      },
    );
    const categorizationColumnItems =
      selectedGroupBlockForTable.categorizationColumnItems.map((item) => {
        return {
          title: (
            <>
              <span>{item.title}</span>
              <LinkIcon
                categorization={item.categorizationWithConfig.categorization}
              />
            </>
          ),
          key: item.key,
          dataIndex: item.key,
          width: item.width,
          render: (_: unknown, record: GroupBlockTableData) => {
            if (record.isPageBreak) {
              return {
                props: { colSpan: 0 },
              };
            }
            return (
              <CategorizationCellSwitch
                key={item.categorizationCellKey}
                disabled={item.disabled}
                categorizationWithConfig={item.categorizationWithConfig}
                groupBlockId={item.groupBlockId}
                index={record.index}
              />
            );
          },
        };
      });

    const items: Array<ColumnType<GroupBlockTableData>> = [
      ...categorizationColumnItems,
      ...entityColumnItems,
    ];

    items.unshift({
      title: "",
      key: "index",
      width: 40,
      fixed: "left",
      render: (_: unknown, props: GroupBlockTableData) => {
        if (props.isPageBreak) {
          return {
            children: (
              <div className="page-break-cell">
                <span className="page-break-text">
                  <i className="bi bi-caret-up-fill icon" />
                  <span>End of page {props.pageNumber! - 1}</span>
                </span>
                <span className="page-break-pill">page {props.pageNumber}</span>
                <span className="page-break-text">
                  <span>Start of page {props.pageNumber}</span>
                  <i className="bi bi-caret-down-fill icon" />
                </span>
              </div>
            ),
            props: {
              colSpan: items.length + 2, // +2 for index and actions columns
            },
          };
        }
        return (
          <div
            onClick={() =>
              highlightEntitiesInGroupBlock(props.index, props.groupBlock)
            }
            className="index-column"
          >
            <h4 className="index-column-text">{props.index}</h4>
          </div>
        );
      },
    });
    items.push(actionColumnItem);

    return items;
  }, [
    disabled,
    selectedGroupBlockForTable,
    actionColumnItem,
    highlightEntitiesInGroupBlock,
  ]);

  if (configMapLoading) {
    return (
      <div className="annotation-table-controls-skeleton">
        <div className="header-skeleton" />
        <div className="body-skeleton">
          <Skeleton active paragraph={{ rows: 5 }} />
        </div>
      </div>
    );
  }

  return (
    <div
      className="annotation-table-controls-container"
      ref={refHeightContainer}
    >
      <GroupBlockTabs
        handleResizing={handleResizing}
        multipleLinesGroupBlocks={multipleLinesGroupBlocks}
      />
      <div className="group-blocks-container">
        <Table
          ref={tableRef}
          className="group-block-table"
          dataSource={selectedGroupBlockForTable?.data}
          size="small"
          pagination={false}
          columns={columnItems}
          rowKey={(record) => record.index}
          virtual
          scroll={{
            y: currentHeight - GROUPBLOCK_TABS_HEIGHT - TABLE_HEADER_HEIGHT,
          }}
          rowClassName={(record) => {
            if (record.isPageBreak) {
              return "page-break-row";
            }
            return record.index === highlightedIndex ? "highlighted-row" : "";
          }}
        />
      </div>
    </div>
  );
};

export default memo(AnnotationTableControls);
